/**
 * useMinistryRole — resolves the current user's Ministry role for the
 * Ministry workspace.
 *
 * Joins workspace_members.role (per-user assignment, free-text column
 * that for Ministry users carries one of the seeded role_keys) against
 * workspace_roles (per-workspace catalog with landing_route +
 * permissions). Returns null while loading or if the user has no role.
 *
 * Why role lives on workspace_members.role rather than the legacy
 * workspace_role column: workspace_role has a CHECK constraint pinned
 * to admin/executive/manager/ic/viewer (the legacy permission tiers).
 * Ministry roles are domain-specific (minister, pmo, legal_reviewer)
 * and don't fit that taxonomy. Splitting them keeps the legacy tier
 * intact for the existing N71 product while the white-label edition
 * gets its own role catalog.
 */

import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useMinistryWorkspace } from "./useMinistryWorkspace";

export interface MinistryRolePermissions {
  view: string[];
  approve: string[];
  write: string[];
}

export interface MinistryRole {
  role_key: string;
  role_label: string;
  landing_route: string;
  permissions: MinistryRolePermissions;
  display_order: number;
}

export function useMinistryRole() {
  const { user } = useAuth();
  const { workspace } = useMinistryWorkspace();
  const [role, setRole] = useState<MinistryRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !workspace) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);

      // 1. Resolve this user's role assignment for the Ministry workspace.
      const { data: membership, error: memErr } = await supabase
        .from("workspace_members")
        .select("role")
        .eq("workspace_id", workspace.id)
        .eq("user_id", user.id)
        .eq("is_active", true)
        .maybeSingle();

      if (cancelled) return;
      if (memErr || !membership?.role) {
        setRole(null);
        setError(memErr?.message ?? "no role assigned");
        setLoading(false);
        return;
      }

      // 2. Look up the role catalog entry (landing_route + permissions).
      const { data: roleRow, error: roleErr } = await supabase
        .from("workspace_roles")
        .select("role_key, role_label, landing_route, permissions, display_order")
        .eq("workspace_id", workspace.id)
        .eq("role_key", membership.role)
        .maybeSingle();

      if (cancelled) return;
      if (roleErr || !roleRow) {
        setRole(null);
        setError(roleErr?.message ?? `role_key '${membership.role}' not in catalog`);
        setLoading(false);
        return;
      }

      const perms = (roleRow.permissions ?? {}) as Partial<MinistryRolePermissions>;
      setRole({
        role_key: roleRow.role_key,
        role_label: roleRow.role_label,
        landing_route: roleRow.landing_route,
        permissions: {
          view: perms.view ?? [],
          approve: perms.approve ?? [],
          write: perms.write ?? [],
        },
        display_order: roleRow.display_order ?? 100,
      });
      setError(null);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [user, workspace]);

  /** Wildcard "*" in any permission list grants access to everything. */
  const hasView = (key: string) =>
    !!role && (role.permissions.view.includes("*") || role.permissions.view.includes(key));
  const hasApprove = (key: string) =>
    !!role && (role.permissions.approve.includes("*") || role.permissions.approve.includes(key));
  const hasWrite = (key: string) =>
    !!role && (role.permissions.write.includes("*") || role.permissions.write.includes(key));

  return { role, loading, error, hasView, hasApprove, hasWrite };
}
